import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  FileUp,
  Upload,
  Database,
} from "lucide-react";
import { NavLink } from "react-router-dom";
import ZCS_logo from "../assets/images/logo-zcs.svg";
import { getData } from "../api/axiosConfig";
import { useAuth } from '../context/AuthContext';

/**
 * Navigation component - simplified without authentication
 */
export const Navigation = () => {
  const [filesCount, setFilesCount] = useState(0);
  const { user } = useAuth();
  const userEmail = user?.email || 'anonymous@user.local';

  useEffect(() => {
    const fetchFilesCount = async () => {
      try {
        // Fetch documents for the authenticated user
        const data = await getData(`/api/documents?user_id=${encodeURIComponent(userEmail)}`);
        if (data?.documents) {
          setFilesCount(data.documents.length || 0);
        } else if (Array.isArray(data)) {
          setFilesCount(data.length || 0);
        }
      } catch (error) {
        console.error("Failed to fetch files count:", error);
        setFilesCount(0);
      }
    };

    fetchFilesCount();
    // Refresh count every 10 seconds
    const interval = setInterval(fetchFilesCount, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Navigation Content */}
      <div className="space-y-2">
        {/* Upload Section */}
        <Card className="shadow-sm border-border/20 bg-gradient-to-br from-card to-card/80 backdrop-blur-md hover:shadow-md transition-all duration-200 hover:border-primary/20">
          <CardHeader className="pb-2 border-b border-border/20">
            <CardTitle className="flex items-center gap-3 text-base font-heading font-semibold">
              <div className="p-1.5 bg-gradient-primary rounded-lg">
                <Upload className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="bg-gradient-primary bg-clip-text text-transparent">
                Upload
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 pb-2">
            <NavLink to="/upload" className="block w-full">
              {({ isActive }) => (
                <Button
                  variant="ghost"
                  className={`w-full justify-start h-8 group transition-all duration-200 hover:scale-[1.01] hover:shadow-sm font-medium ${isActive ? "bg-primary/10 shadow-sm" : ""
                    }`}
                >
                  <div className="flex items-center gap-2 w-full">
                    <div
                      className={`p-1 rounded-md transition-all duration-200 group-hover:scale-105 ${isActive
                        ? "bg-primary/20 shadow-sm"
                        : "bg-primary/10 group-hover:bg-primary/20"
                        }`}
                    >
                      <FileUp
                        className={`w-3 h-3 ${isActive ? "text-primary" : ""}`}
                      />
                    </div>
                    <span
                      className={`text-sm ${isActive ? "text-primary font-semibold" : ""}`}
                    >
                      Upload Files
                    </span>
                  </div>
                </Button>
              )}
            </NavLink>
          </CardContent>
        </Card>

        {/* Data Section */}
        <Card className="shadow-sm border-border/20 bg-gradient-to-br from-card to-card/80 backdrop-blur-md hover:shadow-md transition-all duration-200 hover:border-primary/20">
          <CardHeader className="pb-2 border-b border-border/20">
            <CardTitle className="flex items-center gap-3 text-base font-heading font-semibold">
              <div className="p-1.5 bg-gradient-primary rounded-lg">
                <Database className="w-4 h-4 text-primary-foreground" />
              </div>
              <span className="bg-gradient-primary bg-clip-text text-transparent">
                Data
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-2 pb-2 space-y-1">
            <NavLink to="/files" className="block w-full">
              {({ isActive }) => (
                <Button
                  variant="ghost"
                  className={`w-full justify-start h-8 group transition-all duration-200 hover:scale-[1.01] hover:shadow-sm font-medium ${isActive ? "bg-primary/10 shadow-sm" : ""
                    }`}
                >
                  <div className="flex items-center gap-2 w-full">
                    <div
                      className={`p-1 rounded-md transition-all duration-200 group-hover:scale-105 ${isActive
                        ? "bg-primary/20 shadow-sm"
                        : "bg-primary/10 group-hover:bg-primary/20"
                        }`}
                    >
                      <Database
                        className={`w-3 h-3 ${isActive ? "text-primary" : ""}`}
                      />
                    </div>
                    <span
                      className={`text-sm ${isActive ? "text-primary font-semibold" : ""}`}
                    >
                      Files Management
                    </span>
                    {filesCount > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-auto h-5 px-2 text-xs font-semibold bg-primary/10 text-primary border-0"
                      >
                        {filesCount}
                      </Badge>
                    )}
                  </div>
                </Button>
              )}
            </NavLink>
          </CardContent>
        </Card>

        {/* Footer */}
        <Card className="shadow-sm border-border/20 bg-gradient-to-br from-card to-card/80 backdrop-blur-md">
          <CardContent className="p-2 text-center">
            <div className="text-xs text-muted-foreground mb-1">Powered By</div>
            <div className="flex justify-center">
              <img
                src={ZCS_logo}
                alt="ZCS Logo"
                className="w-16 h-auto opacity-80 hover:opacity-100 transition-opacity duration-200"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
